import React, { useCallback, useEffect, useRef, useState } from 'react';
import { appendRetryParam } from '../../hooks/usePictureImageLoad';

export interface NetworkImageProps {
  src: string;
  alt: string;
  className?: string;
  style?: React.CSSProperties;
  /** When true, defer loading until near the viewport (default: true). */
  lazy?: boolean;
  maxRetries?: number;
}

/**
 * Plain <img> with retry-on-error and lazy-load completion detection.
 * Used for location/banner images that do not use the <picture> loading overlay.
 */
const NetworkImage: React.FC<NetworkImageProps> = ({
  src,
  alt,
  className,
  style,
  lazy = true,
  maxRetries = 2,
}) => {
  const [retryCount, setRetryCount] = useState(0);
  const imgRef = useRef<HTMLImageElement>(null);
  const resolvedSrc = appendRetryParam(src, retryCount);

  const handleError = useCallback(() => {
    if (retryCount < maxRetries) {
      setTimeout(() => {
        setRetryCount((current) => current + 1);
      }, 300 * (retryCount + 1));
    }
  }, [maxRetries, retryCount]);

  useEffect(() => {
    setRetryCount(0);
  }, [src]);

  useEffect(() => {
    const img = imgRef.current;
    if (!img || !lazy) {
      return;
    }

    let rafId = 0;
    let pollCount = 0;
    const maxPolls = 120;

    const stopPolling = () => {
      if (rafId) {
        cancelAnimationFrame(rafId);
        rafId = 0;
      }
      pollCount = 0;
    };

    const reloadIfStuck = () => {
      const current = imgRef.current;
      if (!current) {
        return;
      }
      if (current.complete && current.naturalWidth === 0 && retryCount < maxRetries) {
        handleError();
      }
    };

    const startPolling = () => {
      stopPolling();
      const poll = () => {
        const current = imgRef.current;
        if (!current || pollCount >= maxPolls) {
          stopPolling();
          return;
        }
        pollCount += 1;
        reloadIfStuck();
        rafId = requestAnimationFrame(poll);
      };
      rafId = requestAnimationFrame(poll);
    };

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          reloadIfStuck();
          startPolling();
        } else {
          stopPolling();
        }
      },
      { rootMargin: '200px 0px' },
    );

    observer.observe(img);

    return () => {
      observer.disconnect();
      stopPolling();
    };
  }, [lazy, resolvedSrc, retryCount, maxRetries, handleError]);

  return (
    <img
      ref={imgRef}
      src={resolvedSrc}
      alt={alt}
      className={className}
      style={style}
      loading={lazy ? 'lazy' : 'eager'}
      decoding="async"
      onError={handleError}
    />
  );
};

export default NetworkImage;
